import { Button } from "./ui/button";
import { Menu, X, User, LogOut, Shield, FileEdit, Inbox } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "./NotificationBell";
import { AddChallengeModal } from "./AddChallengeModal";
import logo from "@/assets/logo.png";

const DRAFT_STORAGE_KEY = "challenge_draft";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const unreadCount = useUnreadMessages();
  const navigate = useNavigate();

  // Check for draft on mount and when modal closes
  useEffect(() => {
    const checkDraft = () => {
      const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
      setHasDraft(!!draft);
    };
    
    checkDraft();
    
    // Listen for storage changes (in case draft is modified in another tab)
    window.addEventListener("storage", checkDraft);
    
    return () => window.removeEventListener("storage", checkDraft);
  }, []);

  // Re-check draft when modal closes
  useEffect(() => {
    if (!showChallengeModal) {
      const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
      setHasDraft(!!draft);
    }
  }, [showChallengeModal]);

  const links = [
    { label: "Challenges", href: "/challenges" },
    { label: "Community", href: "/community" },
    { label: "Rankings", href: "/rankings" },
    { label: "Testimonials", href: "/testimonials" },
    { label: "How It Works", href: "/how-it-works" },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handlePostChallenge = () => {
    setIsOpen(false);
    if (user) {
      setShowChallengeModal(true);
    } else {
      navigate("/auth?reason=post_challenge");
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="Comp3t3 Logo" className="w-14 h-14 sm:w-12 sm:h-12 object-contain" />
              <span className="text-xl font-bold text-foreground">
                Comp3t3
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {links.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <NotificationBell />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-2">
                        <User className="w-4 h-4" />
                        Profile
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-popover">
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/inbox" className="cursor-pointer flex items-center">
                          <Inbox className="w-4 h-4 mr-2" />
                          Message Inbox
                          {unreadCount > 0 && (
                            <span className="ml-auto bg-destructive text-destructive-foreground text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="cursor-pointer">
                              <Shield className="w-4 h-4 mr-2" />
                              Admin Dashboard
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/auth">Sign In</Link>
                </Button>
              )}
              <Button variant="hero" size="sm" onClick={handlePostChallenge} className="relative">
                {hasDraft && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                  </span>
                )}
                {hasDraft ? (
                  <>
                    <FileEdit className="w-4 h-4 mr-1" />
                    Continue Draft
                  </>
                ) : (
                  "Post a Challenge"
                )}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-secondary rounded-lg transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <X className="w-5 h-5 text-foreground" />
              ) : (
                <Menu className="w-5 h-5 text-foreground" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div
            className={cn(
              "md:hidden overflow-hidden transition-all duration-300",
              isOpen ? "max-h-96 pb-4" : "max-h-0"
            )}
          >
            <div className="flex flex-col gap-1 pt-2">
              {links.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-4 border-t border-border mt-2">
                {user ? (
                  <>
                    <Button variant="ghost" className="justify-start" asChild>
                      <Link to="/profile" onClick={() => setIsOpen(false)}>
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </Link>
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" className="justify-start" asChild>
                        <Link to="/admin" onClick={() => setIsOpen(false)}>
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </Link>
                      </Button>
                    )}
                    <Button variant="ghost" className="justify-start text-destructive" onClick={() => { handleSignOut(); setIsOpen(false); }}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" className="justify-start" asChild>
                    <Link to="/auth" onClick={() => setIsOpen(false)}>Sign In</Link>
                  </Button>
                )}
                <Button variant="hero" onClick={handlePostChallenge} className="relative">
                  {hasDraft && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                  )}
                  {hasDraft ? (
                    <>
                      <FileEdit className="w-4 h-4 mr-1" />
                      Continue Draft
                    </>
                  ) : (
                    "Post a Challenge"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Add Challenge Modal */}
      {user && (
        <AddChallengeModal
          open={showChallengeModal}
          onOpenChange={setShowChallengeModal}
          userId={user.id}
          onSuccess={() => {
            setShowChallengeModal(false);
            navigate("/challenges");
          }}
        />
      )}
    </>
  );
};
